/**
 * Location state flag set when the template agent navigates away from `/new`
 * after a successful create. TemplateUnsavedPrompt must not block that transition
 * — the abandoned local draft is intentionally discarded.
 *
 * Also holds sessionStorage helpers for create chat persistence + handoff:
 * the create conversation UUID is tracked while on `/new` (survives refresh),
 * then rebound to the saved template id so detail chat loads the same history.
 */ export const TEMPLATE_AGENT_CREATE_NAV_STATE_KEY = 'templateAgentCreateNav';
const CREATE_CONVERSATION_KEY = 'templateAgentCreateConversationId';
const CREATE_CHAT_HANDOFF_KEY = 'templateAgentCreateChatHandoff';
/**
 * Survive React Strict Mode remount only: first consume removes sessionStorage,
 * but the sync remount effect must still see the same handoff id. Cleared on
 * the next microtask so later navigations call getConversations normally.
 */ const strictModeHandoffReplayByTemplateId = new Map();
export const templateAgentCreateNavState = {
    [TEMPLATE_AGENT_CREATE_NAV_STATE_KEY]: true
};
export const isTemplateAgentCreateNav = (state)=>Boolean(state && typeof state === 'object' && state[TEMPLATE_AGENT_CREATE_NAV_STATE_KEY] === true);
/** `/templates/projects/new` or `/templates/projects/folder/:folderId/new`. */ export const isProjectTemplateNewPath = (pathname)=>/\/templates\/projects\/(?:folder\/[^/]+\/)?new(?:\/|$)/.test(pathname);
const canUseSessionStorage = ()=>typeof sessionStorage !== 'undefined';
/**
 * Active create-flow conversation id while on `/templates/projects/new`.
 * Written after the first successful send; read on remount to restore history.
 */ export const setCreateConversationId = (conversationId)=>{
    if (!canUseSessionStorage() || !conversationId) {
        return;
    }
    sessionStorage.setItem(CREATE_CONVERSATION_KEY, conversationId);
};
export const getCreateConversationId = ()=>{
    if (!canUseSessionStorage()) {
        return null;
    }
    const value = sessionStorage.getItem(CREATE_CONVERSATION_KEY);
    return value && value.length > 0 ? value : null;
};
export const clearCreateConversationId = ()=>{
    if (!canUseSessionStorage()) {
        return;
    }
    sessionStorage.removeItem(CREATE_CONVERSATION_KEY);
};
/**
 * Intentional new-draft entry (New CTA). Clears abandoned create chat so a
 * later `/new` visit does not resume the previous conversation. Refresh of an
 * in-progress `/new` does not call this — sessionStorage id is kept.
 */ export const beginFreshCreateDraft = ()=>{
    clearCreateConversationId();
};
/** One-shot handoff consumed when edit chat mounts for `templateId`. */ export const setCreateChatHandoff = (handoff)=>{
    if (!canUseSessionStorage()) {
        return;
    }
    if (!Number.isFinite(handoff.templateId) || handoff.templateId <= 0 || !handoff.conversationId) {
        return;
    }
    strictModeHandoffReplayByTemplateId.delete(handoff.templateId);
    sessionStorage.setItem(CREATE_CHAT_HANDOFF_KEY, JSON.stringify(handoff));
};
export const consumeCreateChatHandoff = (templateId)=>{
    const replay = strictModeHandoffReplayByTemplateId.get(templateId);
    if (replay != null) {
        return replay;
    }
    if (!canUseSessionStorage()) {
        return null;
    }
    const raw = sessionStorage.getItem(CREATE_CHAT_HANDOFF_KEY);
    if (!raw) {
        return null;
    }
    try {
        const parsed = JSON.parse(raw);
        if (parsed.templateId === templateId && typeof parsed.conversationId === 'string' && parsed.conversationId.length > 0) {
            sessionStorage.removeItem(CREATE_CHAT_HANDOFF_KEY);
            strictModeHandoffReplayByTemplateId.set(templateId, parsed.conversationId);
            // Strict Mode remount is synchronous; clear after this turn so a later
            // mount of the same template id does not skip getConversations.
            queueMicrotask(()=>{
                strictModeHandoffReplayByTemplateId.delete(templateId);
            });
            return parsed.conversationId;
        }
    } catch (unused) {
        sessionStorage.removeItem(CREATE_CHAT_HANDOFF_KEY);
    }
    return null;
};
export const clearCreateChatHandoff = ()=>{
    strictModeHandoffReplayByTemplateId.clear();
    if (!canUseSessionStorage()) {
        return;
    }
    sessionStorage.removeItem(CREATE_CHAT_HANDOFF_KEY);
};
/**
 * After Save (or legacy agent create): bind create conversation to template id
 * and stash a one-shot handoff for the edit chat panel. Best-effort — handoff
 * still set even if PATCH fails so SPA can open the same conversation id.
 */ export const handoffCreateChatToTemplate = async (params)=>{
    const conversationId = getCreateConversationId();
    if (!conversationId || params.templateId <= 0) {
        return null;
    }
    try {
        await params.bind({
            conversationId,
            templateId: params.templateId,
            userId: params.userId,
            accountId: params.accountId
        });
    } catch (error) {
        var _params_onBindError;
        (_params_onBindError = params.onBindError) === null || _params_onBindError === void 0 ? void 0 : _params_onBindError.call(params, error);
    }
    setCreateChatHandoff({
        templateId: params.templateId,
        conversationId
    });
    clearCreateConversationId();
    return conversationId;
};
